/**
 * Link href classification used by `TipTapRenderer` consumers.
 *
 * Storage keeps the raw href as a single string (matches TipTap's stock
 * Link extension). On the way out we classify each link into a typed
 * discriminated union so renderers don't have to reinvent the
 * `href.startsWith("/")` / mailto / hash heuristic at every call site.
 *
 * Each app's `Link` override (web `SpaLink`, navigator `NavigatorLink`)
 * receives the parsed value and `switch (parsed.kind)`s on it. Hrefs
 * outside the safe-scheme allowlist (everything other than http(s),
 * relative paths, anchors, mailto, tel) collapse to `{ kind: "unsafe" }`
 * — renderers must NOT navigate to those, since they include vectors
 * like `javascript:`, `data:text/html`, and `vbscript:`.
 */
export type ParsedLinkHref =
    | { kind: "internal"; path: string }
    | { kind: "external"; href: string }
    | { kind: "anchor"; hash: string }
    | { kind: "mailto"; email: string }
    | { kind: "tel"; tel: string }
    | { kind: "unsafe"; raw: string };

const EXTERNAL_PROTOCOLS = ["http://", "https://"] as const;
const SCHEME_RE = /^[a-z][a-z0-9+.-]*:/i;

export function parseLinkHref(raw: string): ParsedLinkHref {
    const trimmed = raw.trim();
    if (!trimmed) return { kind: "unsafe", raw };
    if (trimmed.startsWith("#")) return { kind: "anchor", hash: trimmed };
    if (trimmed.startsWith("mailto:")) {
        return { kind: "mailto", email: trimmed.slice("mailto:".length) };
    }
    if (trimmed.startsWith("tel:")) {
        return { kind: "tel", tel: trimmed.slice("tel:".length) };
    }
    if (EXTERNAL_PROTOCOLS.some((p) => trimmed.toLowerCase().startsWith(p))) {
        return { kind: "external", href: trimmed };
    }
    if (trimmed.startsWith("/")) return { kind: "internal", path: trimmed };
    // Any other scheme — `javascript:`, `data:`, `vbscript:`, `file:`, custom
    // protocol handlers — is rejected. React 19 strips `javascript:` from
    // anchor `href`, but `data:text/html,...` slips through and would land an
    // attacker-controlled page in a new tab when clicked.
    if (SCHEME_RE.test(trimmed)) return { kind: "unsafe", raw: trimmed };
    // Schemeless ("example.com", "//cdn.example.com") — treat as external. The
    // renderer adds `target="_blank" rel="noopener noreferrer"` defensively.
    return { kind: "external", href: trimmed };
}
